import axiosInstance from '@/services/interceptor.services';
import type {
  IDevice,
  IDeviceBody,
  IDeviceGroup,
  IDeviceGroupBody,
  IDeviceType,
  IDeviceUpdateBody,
  IEquipmentPage,
  IEquipmentQuery,
  ISite,
  ISiteBody,
} from '@/types/equipment.types';

const HEADERS = { 'x-client-app': 'admin-back-office' };
const storeSites = (storeId: string) => `restaurants/${storeId}/sites`;

// Un tableau part en clé répétée (storeIds=a&storeIds=b), la forme que le serveur lit ; les valeurs vides sont omises.
export const buildEquipmentParams = (query: IEquipmentQuery) => {
  const params = new URLSearchParams({ pagination: 'true' });
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) value.forEach((v) => params.append(key, v));
    else if (value !== undefined && value !== '')
      params.append(key, String(value));
  }
  return params;
};

// Le serveur enveloppe les réponses unitaires et le catalogue dans { data, message } ; la doc d'API les montre nues.
// Les deux formes passent : on prend `data` quand il y est.
const unwrap = <T>(body: unknown): T =>
  ((body as { data?: unknown } | null)?.data ?? body) as T;

type RawDeviceType = Partial<IDeviceType> & Pick<IDeviceType, 'type' | 'label'>;

// Un catalogue plus ancien ne dit ni MAC, ni port, ni réglages : ce que le type n'annonce pas est « rien de particulier ».
export const toDeviceType = (raw: RawDeviceType): IDeviceType => {
  const networked = raw.networked ?? true;
  return {
    type: raw.type,
    label: raw.label,
    networked,
    mac: raw.mac ?? (networked ? 'optional' : 'none'),
    port: raw.port,
    settings: raw.settings ?? [],
    master: raw.master ?? false,
    roles: raw.roles ?? [],
  };
};

const search = async <T>(path: string, query: IEquipmentQuery) =>
  (
    await axiosInstance.get<IEquipmentPage<T>>(
      `${path}?${buildEquipmentParams(query)}`,
      { headers: HEADERS },
    )
  ).data;

const send = async <T>(
  method: 'post' | 'put',
  path: string,
  body: unknown,
): Promise<T> =>
  unwrap<T>(
    (await axiosInstance[method](path, body, { headers: HEADERS })).data,
  );

const remove = (path: string) =>
  axiosInstance.delete(path, { headers: HEADERS });

const onSite = (storeId: string, siteId: string) =>
  `${storeSites(storeId)}/${siteId}`;

export const EquipmentService = {
  searchSites: (query: IEquipmentQuery) => search<ISite>('sites/search', query),
  createSite: (storeId: string, body: ISiteBody) =>
    send<ISite>('post', storeSites(storeId), body),
  updateSite: (storeId: string, siteId: string, body: ISiteBody) =>
    send<ISite>('put', onSite(storeId, siteId), body),
  deleteSite: (storeId: string, siteId: string) =>
    remove(onSite(storeId, siteId)),

  deviceTypes: async (): Promise<IDeviceType[]> => {
    const body = (await axiosInstance.get('device-types', { headers: HEADERS }))
      .data;
    const list = unwrap<RawDeviceType[]>(body);
    return Array.isArray(list) ? list.map(toDeviceType) : [];
  },
  searchDevices: (query: IEquipmentQuery) =>
    search<IDevice>('devices/search', query),
  createDevice: (storeId: string, siteId: string, body: IDeviceBody) =>
    send<IDevice>('post', `${onSite(storeId, siteId)}/devices`, body),
  updateDevice: (
    storeId: string,
    siteId: string,
    deviceId: string,
    body: IDeviceUpdateBody,
  ) =>
    send<IDevice>(
      'put',
      `${onSite(storeId, siteId)}/devices/${deviceId}`,
      body,
    ),
  deleteDevice: (storeId: string, siteId: string, deviceId: string) =>
    remove(`${onSite(storeId, siteId)}/devices/${deviceId}`),

  searchGroups: (query: IEquipmentQuery) =>
    search<IDeviceGroup>('device-groups/search', query),
  createGroup: (storeId: string, siteId: string, body: IDeviceGroupBody) =>
    send<IDeviceGroup>(
      'post',
      `${onSite(storeId, siteId)}/device-groups`,
      body,
    ),
  updateGroup: (
    storeId: string,
    siteId: string,
    groupId: string,
    body: IDeviceGroupBody,
  ) =>
    send<IDeviceGroup>(
      'put',
      `${onSite(storeId, siteId)}/device-groups/${groupId}`,
      body,
    ),
  deleteGroup: (storeId: string, siteId: string, groupId: string) =>
    remove(`${onSite(storeId, siteId)}/device-groups/${groupId}`),
};
